import { z } from 'zod';

import { zCountryCode } from './billing';

import { LOCALES, THEMES } from '../enums';

/**
 * Логин: буквы, цифры, точка, дефис, подчёркивание.
 * Регистр не важен — в базе храним как ввёл пользователь, сравниваем в нижнем.
 */
export const USERNAME_PATTERN = /^[a-zA-Z0-9._-]{3,32}$/;
export const MIN_PASSWORD_LENGTH = 8;

export const zUsername = z
  .string()
  .trim()
  .min(3, { message: 'validation.usernameTooShort' })
  .max(32, { message: 'validation.usernameTooLong' })
  .regex(USERNAME_PATTERN, { message: 'validation.usernameFormat' });

export const zPassword = z
  .string()
  .min(MIN_PASSWORD_LENGTH, { message: 'validation.passwordTooShort' })
  .max(128, { message: 'validation.passwordTooLong' });

export const registerSchema = z.object({
  username: zUsername,
  password: zPassword,
  displayName: z.string().trim().max(80).optional(),
  locale: z.enum(LOCALES).default('ru'),
  /**
   * Регион аккаунта, ISO 3166-1 alpha-2. Обязателен и выбирается человеком:
   * определить его по адресу, языку или часовому поясу — значит записать в
   * финансовые данные догадку. Валюта расчётов выводится из региона и
   * отдельно не выбирается.
   */
  countryCode: zCountryCode,
  /** Обязателен, когда регистрация в режиме INVITE_ONLY. */
  inviteCode: z.string().trim().max(64).optional(),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  username: z.string().trim().min(1).max(32),
  password: z.string().min(1).max(128),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(128),
  newPassword: zPassword,
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/** Восстановление доступа без почты — по коду, выданному при регистрации. */
export const recoverSchema = z.object({
  username: z.string().trim().min(1).max(32),
  recoveryCode: z.string().trim().min(8).max(64),
  newPassword: zPassword,
});
export type RecoverInput = z.infer<typeof recoverSchema>;

export const updateProfileSchema = z.object({
  displayName: z.string().trim().min(1).max(80).optional(),
  locale: z.enum(LOCALES).optional(),
  theme: z.enum(THEMES).optional(),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

/**
 * Оценка надёжности пароля для индикатора в форме.
 * Возвращает 0..4 и список того, чего не хватает.
 */
export function passwordStrength(password: string): { score: 0 | 1 | 2 | 3 | 4; hints: string[] } {
  const hints: string[] = [];
  let score = 0;

  if (password.length >= MIN_PASSWORD_LENGTH) score += 1;
  else hints.push('password.hintLength');

  if (/[a-zа-я]/.test(password) && /[A-ZА-Я]/.test(password)) score += 1;
  else hints.push('password.hintCase');

  if (/\d/.test(password)) score += 1;
  else hints.push('password.hintDigit');

  if (/[^\w\s]/.test(password)) score += 1;
  else hints.push('password.hintSymbol');

  if (password.length >= 14) score = Math.min(4, score + 1);

  return { score: Math.min(4, score) as 0 | 1 | 2 | 3 | 4, hints };
}

/** Короткий список самых частых паролей — отклоняем их при регистрации. */
export const COMMON_PASSWORDS = new Set([
  '12345678', '123456789', '1234567890', 'password', 'password1', 'password123',
  'qwerty123', 'qwertyui', '11111111', '00000000', 'iloveyou', 'admin123',
  'user1234', 'welcome1', 'abc12345', 'letmein1', 'football', 'baseball',
  'sunshine', 'princess', 'dragon123', 'monkey123', 'superman', 'trustno1',
  'qazwsxedc', 'zxcvbnm1', 'asdfghjk', 'p@ssword', 'passw0rd', '87654321',
]);

export function isCommonPassword(password: string): boolean {
  return COMMON_PASSWORDS.has(password.toLowerCase());
}
